// TEMPORÁRIO — mock local do Portal do Credenciado, remover no desenvolvimento do backend.
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import { resolverMock } from '@/modulos/portal-credenciado/comum/mock';
import { diaDaqui, validadeNoPreset } from '@/modulos/portal-credenciado/comum/vencimento';
import { normalizarBusca } from '@/nucleo/utilitarios';

import type { Condutor, FiltroDeCondutores } from './contrato';
import dadosMock from './dados.mock.json';

/**
 * Camada de dados de "Meus Condutores".
 *
 * Mesmo desenho das demais telas do sistema: nenhum componente busca dado
 * direto, a chave de cache mora junto de quem a usa. A diferença para uma
 * listagem real (ex. `operacao/condutores/dados.ts`) é que aqui não há
 * `chamarApi` nenhum — filtro e paginação são resolvidos aqui mesmo, sobre o
 * array fixo de `dados.mock.json` — ver a justificativa em `comum/mock.ts`.
 */
export const CHAVES_DE_CONDUTORES = {
  lista: (filtro: FiltroDeCondutores) => ['credenciado', 'condutores', 'lista', filtro] as const,
  detalhe: (slug: string) => ['credenciado', 'condutores', 'detalhe', slug] as const,
};

/**
 * NENHUMA DATA LITERAL NO MOCK. O arquivo guarda a distância em dias até hoje
 * (`*_em_dias`, negativo é passado), não "2026-09-05": data fixa envelhece
 * sozinha — a CNH "vencendo" de hoje vira "vencida" no mês que vem, o filtro
 * "Vence em 30 dias" esvazia e a demonstração à SMT mostra um condutor que
 * ninguém cadastrou assim.
 */
type CamposDeData =
  | 'cnh_validade'
  | 'crmc_validade'
  | 'curso_primeiro_socorros_validade'
  | 'curso_transporte_escolar_validade'
  | 'curso_mobilidade_validade'
  | 'data_vencimento_atestado_medico';

type CondutorDoMock = Omit<Condutor, CamposDeData> & {
  [Campo in CamposDeData as `${Campo}_em_dias`]: number | null;
};

function emData(dias: number | null, hoje: Date): string | null {
  return dias === null ? null : diaDaqui(dias, hoje);
}

function comoCondutor(item: CondutorDoMock, hoje: Date): Condutor {
  const {
    cnh_validade_em_dias,
    crmc_validade_em_dias,
    curso_primeiro_socorros_validade_em_dias,
    curso_transporte_escolar_validade_em_dias,
    curso_mobilidade_validade_em_dias,
    data_vencimento_atestado_medico_em_dias,
    ...resto
  } = item;
  return {
    ...resto,
    cnh_validade: emData(cnh_validade_em_dias, hoje),
    crmc_validade: emData(crmc_validade_em_dias, hoje),
    curso_primeiro_socorros_validade: emData(curso_primeiro_socorros_validade_em_dias, hoje),
    curso_transporte_escolar_validade: emData(curso_transporte_escolar_validade_em_dias, hoje),
    curso_mobilidade_validade: emData(curso_mobilidade_validade_em_dias, hoje),
    data_vencimento_atestado_medico: emData(data_vencimento_atestado_medico_em_dias, hoje),
  };
}

/**
 * Recalculado a cada consulta: a sessão que atravessa a meia-noite não fica
 * com o "hoje" de ontem. Em ordem de nome, como o protótipo pede ao banco.
 */
function condutoresDeHoje(): Condutor[] {
  const hoje = new Date();
  return (dadosMock as CondutorDoMock[])
    .map((item) => comoCondutor(item, hoje))
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

function aplicarFiltro(itens: Condutor[], filtro: FiltroDeCondutores): Condutor[] {
  return itens.filter((condutor) => {
    if (filtro.cnh && !validadeNoPreset(condutor.cnh_validade, filtro.cnh)) {
      return false;
    }

    if (filtro.busca) {
      const alvo = normalizarBusca(filtro.busca);
      const cpfBuscado = filtro.busca.replace(/\D/g, '');
      const combinaPorNome = normalizarBusca(condutor.nome).includes(alvo);
      // Mesmo corte do protótipo: CPF só entra na busca a partir de 3 dígitos.
      const combinaPorCpf = cpfBuscado.length >= 3 && condutor.cpf.includes(cpfBuscado);
      if (!combinaPorNome && !combinaPorCpf) {
        return false;
      }
    }

    return true;
  });
}

export function useCondutores(filtro: FiltroDeCondutores): UseQueryResult<Pagina<Condutor>, Error> {
  return useQuery<Pagina<Condutor>, Error>({
    queryKey: CHAVES_DE_CONDUTORES.lista(filtro),
    queryFn: () =>
      resolverMock(
        paginar(aplicarFiltro(condutoresDeHoje(), filtro), filtro.pagina, filtro.tamanho),
      ),
    // Mantém a página anterior na tela enquanto a próxima chega: sem isso a
    // tabela some e volta a cada clique na paginação.
    placeholderData: (anterior) => anterior,
  });
}

export function useCondutor(slug: string | undefined): UseQueryResult<Condutor | null, Error> {
  return useQuery<Condutor | null, Error>({
    queryKey: CHAVES_DE_CONDUTORES.detalhe(slug ?? ''),
    queryFn: () =>
      resolverMock(condutoresDeHoje().find((condutor) => condutor.slug === slug) ?? null),
    enabled: !!slug,
  });
}
